import { useCallback, useRef } from 'react';
import OSS from 'ali-oss';
import { applyApi } from '../services/apply.api';
import { ApplyOssStsToken } from '@packages/global';

let cachedToken: ApplyOssStsToken | null = null;
let tokenExpiry: number = 0;

function isTokenValid(): boolean {
  return cachedToken !== null && (tokenExpiry - Date.now()) > 5 * 60 * 1000;
}

export interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
}

export interface UseApplyOssDirectUploadOptions {
  prefix?: string;
  onProgress?: (progress: UploadProgress) => void;
}

export interface UseApplyOssDirectUploadReturn {
  upload: (file: File) => Promise<string>;
  getToken: () => Promise<ApplyOssStsToken>;
  isUploading: boolean;
}

export function useApplyOssDirectUpload(
  options: UseApplyOssDirectUploadOptions = {}
): UseApplyOssDirectUploadReturn {
  const { prefix = 'fancypeter/apply', onProgress } = options;
  const isUploadingRef = useRef(false);

  const getToken = useCallback(async (): Promise<ApplyOssStsToken> => {
    if (isTokenValid()) {
      return cachedToken!;
    }

    const token = await applyApi.getStsToken();
    cachedToken = token;
    tokenExpiry = new Date(token.expiration).getTime();
    return token;
  }, []);

  const upload = useCallback(
    async (file: File): Promise<string> => {
      if (isUploadingRef.current) {
        throw new Error('Upload already in progress');
      }

      isUploadingRef.current = true;

      try {
        const token = await getToken();

        const client = new OSS({
          accessKeyId: token.accessKeyId,
          accessKeySecret: token.accessKeySecret,
          stsToken: token.securityToken,
          bucket: token.bucket,
          region: token.region,
        });

        const ossPath = generateOssPath(file, prefix);

        const result = await client.multipartUpload(ossPath, file, {
          progress: (p: number) => {
            if (onProgress) {
              onProgress({
                percent: Math.round(p * 100),
                loaded: p * file.size,
                total: file.size,
              });
            }
          },
          headers: {
            'x-oss-object-acl': 'public-read',
          },
        });

        return `https://${token.bucket}.${token.region}.aliyuncs.com/${ossPath}`;
      } finally {
        isUploadingRef.current = false;
      }
    },
    [getToken, prefix, onProgress]
  );

  return {
    upload,
    getToken,
    get isUploading() {
      return isUploadingRef.current;
    },
  };
}

function generateOssPath(file: File, prefix: string): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const lastDot = file.name.lastIndexOf('.');
  const ext = lastDot > 0 ? file.name.slice(lastDot).toLowerCase() : '';

  return `${prefix}/videos/${year}/${month}-${day}/${crypto.randomUUID()}${ext}`;
}
